import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import { Resolver, useForm } from 'react-hook-form';
import { initialForgotPasswordFormValues } from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { EButtonVariants } from 'constants/Buttons';
import { RouterLink } from 'components/atoms/RouterLink';
import { IForgotPasswordBodyRequest } from 'models/password';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { ERouteLinks } from 'models/route';
import { Box } from '@mui/system';
import { AuthTemplate, StyledAuthHeader } from 'components/templates/AuthTemplate';
import { usePassword } from 'models/passwordContext';
import { changeForgotPasswordRecaptcha } from 'requests/auth';
import { colors } from 'theme/colors';
import useForgotPasswordFormSchema from 'pages/ForgotPasswordPage/schemaValidation';
import { RecaptchaGate } from 'state/contexts/RecaptchaGate';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useTranslations } from 'hooks/useTranslations';
import {
  StyledLanguageSelectOuterWrapper,
  StyledLanguageSelectWrapper,
} from 'components/templates/AuthTemplate/styles';
import { LanguageSelect } from 'components/molecules/LanguageSelect';
import { AuthLogoContainer } from 'components/organisms/AuthLogoContainer';

const ForgotPasswordPage = (): ReactElement => {
  const [formStep, setFormStep] = useState(1);
  const { schema } = useForgotPasswordFormSchema();
  const { t } = useTranslations();
  const [recaptchaError, setRecaptchaError] = useState<string>('');
  const [isBannedAccountError, setIsBannedAccountError] = useState<boolean>(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [isRecaptchaVerifiedSuccessfully, setIsRecaptchaVerifiedSuccessfully] =
    useState<boolean>(false);

  useEffect(() => setFormStep(1), []);

  useEffect(() => {
    (async function handleRecaptchaValidation() {
      if (recaptchaToken && !isRecaptchaVerifiedSuccessfully) {
        setRecaptchaError('');
        const res = await changeForgotPasswordRecaptcha(recaptchaToken);
        if (res && Object.prototype.hasOwnProperty.call(res, 'success')) {
          setIsRecaptchaVerifiedSuccessfully(res.success);
        }
      }
    })();
  }, [recaptchaToken]);

  const toggleCaptchaBadge = (show: boolean) => {
    const badge = document.getElementsByClassName('grecaptcha-badge')[0];
    if (badge && badge instanceof HTMLElement) {
      badge.style.visibility = show ? 'visible' : 'hidden';
    }
  };

  useEffect(() => {
    toggleCaptchaBadge(true);
    return () => toggleCaptchaBadge(false);
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IForgotPasswordBodyRequest>({
    defaultValues: initialForgotPasswordFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IForgotPasswordBodyRequest>,
  });
  const { forgotPassword, isForgotPasswordLoading } = usePassword();

  const onSubmit = handleSubmit(async (data: IForgotPasswordBodyRequest) => {
    if ((formStep === 1 && isRecaptchaVerifiedSuccessfully) || formStep === 2) {
      forgotPassword(
        data,
        () => setFormStep(2),
        () => setIsBannedAccountError(true),
      );
    } else {
      setRecaptchaError(t('forgotPassword.verifyNotRobot.label'));
    }
  });

  const handleChangeRecaptchaToken = useCallback((token: string) => {
    setRecaptchaToken(token);
  }, []);

  const renderForgotPasswordFormStep = (step: number) => {
    if (step === 1) {
      return (
        <>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
              {t('forgotPassword.step1.title1.label')}
              <br />
              {t('forgotPassword.step1.title2.label')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box>
              <FormInput
                name="email"
                label={t('forgotPassword.step1.email.label')}
                placeholder="name@domain.com"
                control={control}
                errors={errors}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: { xs: 32, sm: 40 },
                  },
                }}
              />
              {recaptchaError && (
                <Box sx={{ minHeight: '24px' }}>
                  <Typography variant="caption" sx={{ color: colors.error700 }}>
                    {recaptchaError}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </>
      );
    }
    if (step === 2) {
      return (
        <Grid size={{ xs: 12 }}>
          <Typography
            variant="body1"
            sx={{
              mt: {
                xs: 0,
                sm: 5,
              },
              maxWidth: '290px',
              margin: 'auto',
              textAlign: 'center',
            }}
          >
            {t('forgotPassword.step2.title1.label')}
            <br />
            {t('forgotPassword.step2.title2.label')}
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'center', mt: 2, mb: 4 }}>
            {t('forgotPassword.step2.linkValid24h.label')}
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'center' }}>
            {t('forgotPassword.step2.didntReceiveEmail.label')}
          </Typography>
        </Grid>
      );
    }
    return null;
  };

  return (
    <AuthTemplate>
      <RecaptchaGate onVerify={handleChangeRecaptchaToken} />
      <Grid
        container
        size={{ xs: 12, sm: 10, md: 7, lg: 6, xl: 6 }}
        sx={{
          display: 'grid',
          py: 2,
          px: { xs: 2, xl: 20 },
          justifyContent: 'center',
          justifyItems: 'center',
          alignItems: 'flex-start',
          alignContent: 'stretch',
          pb: 5,
        }}
      >
        <Grid size={{ xs: 12 }}>
          <StyledLanguageSelectOuterWrapper>
            <StyledLanguageSelectWrapper>
              <LanguageSelect isVisible />
            </StyledLanguageSelectWrapper>
          </StyledLanguageSelectOuterWrapper>
          <AuthLogoContainer />
        </Grid>

        <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'center' }}>
          {isBannedAccountError && (
            <Grid size={{ xs: 12 }} sx={{ mb: 3 }}>
              <Alert
                variant={EAlertVariants.error}
                text={t('forgotPassword.isAccountError.label')}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12 }} sx={{ mt: 4 }}>
            <StyledAuthHeader style={{ marginBottom: '32px' }}>
              {t('forgotPassword.forgotPassword.label')}
            </StyledAuthHeader>
          </Grid>
          {renderForgotPasswordFormStep(formStep)}
          <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'center' }}>
            <Grid size={{ xs: 12 }} sx={{ my: 3 }}>
              <Button
                label={
                  formStep === 1
                    ? t('forgotPassword.resetPassword.button')
                    : t('forgotPassword.resendLink.button')
                }
                variant={formStep === 1 ? EButtonVariants.contained : EButtonVariants.outlined}
                onClick={() => onSubmit()}
                isLoading={isForgotPasswordLoading}
                disabled={!isRecaptchaVerifiedSuccessfully}
                fullWidth
              />
            </Grid>
            <RouterLink
              to={ERouteLinks.AuthWithSlash}
              label={t('forgotPassword.returnLogin.button')}
              replace
            />
          </Grid>
        </Grid>
      </Grid>
    </AuthTemplate>
  );
};
export { ForgotPasswordPage };
